import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BiltEvent, EventPricing, EventRegistration } from '../../types/event';
import RegistrationStatusBadge from '../ui/RegistrationStatusBadge';
import Button from '../ui/Button';
import apiService from '../../services/ApiService';
import {
  builderNextAction,
  builderStatusMessage,
  formatPaymentDeadline,
  formatPriceCents,
  isPaidEntryMode,
} from '../../utils/registrationStatus';

interface RegistrationStatusPanelProps {
  event: BiltEvent;
  registration: EventRegistration;
  pricing?: EventPricing | null;
  onUpdated?: (registration: EventRegistration) => void;
}

const RegistrationStatusPanel: React.FC<RegistrationStatusPanelProps> = ({
  event,
  registration,
  pricing,
  onUpdated,
}) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localReg, setLocalReg] = useState(registration);

  const entryMode = event.entry_mode;
  const paid = isPaidEntryMode(entryMode);
  const nextAction = localReg.next_action || builderNextAction(localReg.status, entryMode);
  const message = builderStatusMessage(localReg.status, entryMode, {
    publicRejectNote: localReg.public_reject_note,
    paymentDeadlineAt: localReg.payment_deadline_at,
  });
  const deadline = formatPaymentDeadline(localReg.payment_deadline_at);
  const canWithdraw = ['pending_approval', 'approved', 'payment_failed'].includes(localReg.status);
  const entryToken = localReg.entry_token || null;
  const showEntryCode = Boolean(entryToken) && ['confirmed', 'checked_in'].includes(localReg.status);

  const vehicleLabel = localReg.vehicle
    ? localReg.vehicle.nickname ||
      `${localReg.vehicle.year} ${localReg.vehicle.make} ${localReg.vehicle.model}`.trim()
    : 'Your vehicle';

  const handlePay = async () => {
    try {
      setBusy(true);
      setError(null);
      const { checkout_url } = await apiService.startRegistrationCheckout(localReg.id);
      window.location.href = checkout_url;
    } catch (err: any) {
      setError(err.message || 'Could not start payment');
    } finally {
      setBusy(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Withdraw this registration?')) return;
    try {
      setBusy(true);
      setError(null);
      const updated = await apiService.withdrawRegistration(localReg.id);
      setLocalReg(updated);
      onUpdated?.(updated);
    } catch (err: any) {
      setError(err.message || 'Could not withdraw');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card p-5 sm:p-6 mb-8" aria-labelledby={`reg-status-${localReg.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 id={`reg-status-${localReg.id}`} className="heading-card mb-1">
            Your registration
          </h2>
          <p className="text-sm text-muted">{vehicleLabel}</p>
          {localReg.class?.name || localReg.class_name ? (
            <p className="text-xs text-gray-500 mt-0.5">{localReg.class?.name || localReg.class_name}</p>
          ) : null}
        </div>
        <RegistrationStatusBadge status={localReg.status} label={localReg.label} />
      </div>

      <p className="text-sm text-muted leading-relaxed mb-4">{message}</p>

      {deadline && (localReg.status === 'approved' || localReg.status === 'payment_failed') && paid ? (
        <p className="text-xs text-amber-300/90 mb-4">
          Payment due by {deadline}
        </p>
      ) : null}

      {paid && pricing?.vehicle_entry_price_cents ? (
        <p className="text-xs text-gray-500 mb-4">
          Entry fee: {formatPriceCents(pricing.vehicle_entry_price_cents, pricing.currency || 'usd')}
        </p>
      ) : null}

      {pricing?.refund_policy_text ? (
        <p className="text-xs text-gray-500 mb-4">Refund policy: {pricing.refund_policy_text}</p>
      ) : null}

      {showEntryCode ? (
        <div className="mb-4 rounded-xl border border-orange-500/30 bg-orange-950/20 p-4 text-left">
          <p className="text-xs uppercase tracking-wide text-orange-200/80 mb-2">Gate entry code</p>
          <p className="font-mono text-sm break-all text-orange-100" aria-label="Vehicle entry code">
            {entryToken}
          </p>
          <p className="text-xs text-gray-400 mt-2 leading-relaxed">
            Show this code at the gate. It is not your vehicle profile QR.
          </p>
        </div>
      ) : null}

      {error ? (
        <div className="alert-error mb-4 text-sm" role="alert">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        {nextAction === 'pay' ? (
          <Button variant="primary" loading={busy} onClick={handlePay} className="min-h-[44px]">
            Complete payment
          </Button>
        ) : null}
        {nextAction === 'confirming' ? (
          <Button
            variant="secondary"
            onClick={() =>
              navigate(`/events/${event.slug}/payment/return?registration_id=${localReg.id}`)
            }
            className="min-h-[44px]"
          >
            View payment status
          </Button>
        ) : null}
        {canWithdraw ? (
          <Button variant="ghost" loading={busy} onClick={handleWithdraw} className="min-h-[44px]">
            Withdraw registration
          </Button>
        ) : null}
      </div>
    </section>
  );
};

export default RegistrationStatusPanel;
